import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface Business {
  id: string; name: string; slug: string; description?: string;
  address?: string; city?: string; phone?: string; email?: string;
  website?: string; logo?: string; coverImage?: string; category?: string;
  instagram?: string; facebook?: string; tiktok?: string; whatsapp?: string;
  gallery?: string[]; currency: string;
  services: Service[]; staff: Staff[]; events: Event[];
}
interface Service { id: string; name: string; description?: string; duration: number; price: number; color: string; }
interface Staff { id: string; firstName: string; lastName: string; bio?: string; avatar?: string; }
interface Event { id: string; title: string; description?: string; startTime: string; endTime: string; maxCapacity: number; service?: { name: string; price: number }; staff?: { firstName: string; lastName: string }; }

const SYM: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', MAD: 'DH' };

function fmt(d: string) { return new Date(d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Madrid' }); }
function fmtT(d: string) { return new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }); }

interface BookingForm { name: string; phone: string; email: string; notes: string; }

export function PublicBusiness() {
  const { slug } = useParams<{ slug: string }>();
  const [biz, setBiz] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<'eventos'|'servicios'|'equipo'|'galeria'>('eventos');
  const [bookingEvent, setBookingEvent] = useState<Event | null>(null);
  const [bookingService, setBookingService] = useState<Service | null>(null);
  const [form, setForm] = useState<BookingForm>({ name: '', phone: '', email: '', notes: '' });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    axios.get(`${API_URL}/businesses/public/${slug}`)
      .then(r => setBiz(r.data.data.business))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleBook = async () => {
    if (!form.name || !form.email) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/availability/book`, {
        businessId: biz?.id,
        eventId: bookingEvent?.id,
        serviceId: bookingService?.id,
        clientName: form.name,
        clientEmail: form.email,
        clientPhone: form.phone,
        notes: form.notes,
      });
      setDone(true);
      setBookingEvent(null);
      setBookingService(null);
      setForm({ name: '', phone: '', email: '', notes: '' });
    } catch { alert('Error al realizar la reserva. Inténtalo de nuevo.'); }
    finally { setSending(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
        <p style={{ color: '#64748b' }}>Cargando...</p>
      </div>
    </div>
  );

  if (notFound || !biz) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, background: '#f8fafc' }}>
      <div style={{ fontSize: 64 }}>🔍</div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>Negocio no encontrado</h1>
      <p style={{ color: '#64748b' }}>El enlace que buscas no existe.</p>
      <a href="https://fitpulse.es" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>← Volver a FitPulse</a>
    </div>
  );

  const sym = SYM[biz.currency] || '€';
  const gallery = Array.isArray(biz.gallery) ? biz.gallery : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Inter',sans-serif;background:#f1f5f9;color:#1e293b}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}

        /* NAVBAR */
        .pub-navbar{background:white;border-bottom:1px solid #e2e8f0;padding:0 5%;height:56px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:50;box-shadow:0 1px 3px rgba(0,0,0,0.06)}
        .pub-navbar-logo{font-weight:800;font-size:1.1rem;color:#3b82f6;text-decoration:none}
        .pub-navbar-cta{background:#3b82f6;color:white;font-weight:600;font-size:.82rem;padding:7px 16px;border-radius:8px;text-decoration:none;transition:background .2s}
        .pub-navbar-cta:hover{background:#2563eb}

        /* HERO */
        .pub-cover{position:relative;height:280px;background:linear-gradient(135deg,#1e293b,#0f172a);overflow:hidden}
        .pub-cover img{width:100%;height:100%;object-fit:cover;opacity:.7}
        .pub-cover-overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.6) 0%,transparent 60%)}
        .pub-header{background:white;border-bottom:1px solid #e2e8f0}
        .pub-header-inner{max-width:960px;margin:0 auto;padding:0 24px;display:flex;align-items:flex-end;gap:20px;position:relative;transform:translateY(-40px);margin-bottom:-20px}
        .pub-logo-wrap{width:88px;height:88px;border-radius:20px;background:linear-gradient(135deg,#3b82f6,#8b5cf6);border:4px solid white;display:flex;align-items:center;justify-content:center;font-size:2rem;font-weight:800;color:white;overflow:hidden;flex-shrink:0;box-shadow:0 8px 24px rgba(0,0,0,.15)}
        .pub-logo-wrap img{width:100%;height:100%;object-fit:cover}
        .pub-header-info{flex:1;padding-bottom:16px}
        .pub-category{font-size:.75rem;font-weight:600;color:#3b82f6;text-transform:uppercase;letter-spacing:.08em;margin-bottom:4px}
        .pub-name{font-size:1.6rem;font-weight:800;color:#1e293b;line-height:1.1;margin-bottom:6px}
        .pub-meta{display:flex;gap:16px;flex-wrap:wrap}
        .pub-meta-item{display:flex;align-items:center;gap:5px;font-size:.8rem;color:#64748b}

        /* SOCIAL */
        .pub-socials{display:flex;gap:8px;margin-top:8px}
        .pub-social{width:32px;height:32px;border-radius:8px;background:#f1f5f9;border:1px solid #e2e8f0;display:flex;align-items:center;justify-content:center;text-decoration:none;font-size:.9rem;transition:background .2s}
        .pub-social:hover{background:#e2e8f0}

        /* TABS */
        .pub-tabs{background:white;border-bottom:1px solid #e2e8f0;position:sticky;top:56px;z-index:40}
        .pub-tabs-inner{max-width:960px;margin:0 auto;padding:0 24px;display:flex;gap:4px}
        .pub-tab{padding:14px 20px;font-size:.88rem;font-weight:600;color:#64748b;border:none;background:none;cursor:pointer;border-bottom:2px solid transparent;transition:all .2s;white-space:nowrap}
        .pub-tab.active{color:#3b82f6;border-bottom-color:#3b82f6}
        .pub-tab:hover{color:#1e293b}

        /* BODY */
        .pub-body{max-width:960px;margin:0 auto;padding:32px 24px;animation:fadeUp .3s ease}

        /* SUCCESS */
        .success-box{background:#dcfce7;border:1px solid #86efac;border-radius:14px;padding:20px 24px;display:flex;align-items:center;gap:14px;color:#15803d;font-weight:600;margin-bottom:24px;animation:fadeUp .3s ease}

        /* EVENTS */
        .events-grid{display:flex;flex-direction:column;gap:16px}
        .event-card{background:white;border-radius:20px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.06);border:1px solid #e2e8f0;display:flex;transition:transform .2s,box-shadow .2s}
        .event-card:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.1)}
        .event-date-col{background:linear-gradient(135deg,#3b82f6,#6366f1);color:white;padding:20px 16px;display:flex;flex-direction:column;align-items:center;justify-content:center;min-width:80px;text-align:center}
        .event-day{font-size:2rem;font-weight:800;line-height:1}
        .event-mon{font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.08em;opacity:.85;margin-top:2px}
        .event-body{flex:1;padding:20px 24px;display:flex;flex-direction:column;justify-content:space-between}
        .event-title{font-size:1.05rem;font-weight:700;color:#1e293b;margin-bottom:6px}
        .event-desc{font-size:.85rem;color:#64748b;line-height:1.5;margin-bottom:12px}
        .event-tags{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px}
        .tag{font-size:.72rem;font-weight:600;padding:4px 10px;border-radius:100px}
        .tag-blue{background:#dbeafe;color:#1d4ed8}
        .tag-gray{background:#f1f5f9;color:#475569}
        .tag-green{background:#dcfce7;color:#15803d}
        .event-footer{display:flex;align-items:center;justify-content:space-between}
        .event-time{font-size:.8rem;color:#94a3b8;display:flex;align-items:center;gap:4px}

        /* SERVICES */
        .services-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:16px}
        .service-card{background:white;border-radius:16px;padding:20px;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,.04);transition:transform .2s,box-shadow .2s}
        .service-card:hover{transform:translateY(-2px);box-shadow:0 8px 20px rgba(0,0,0,.08)}
        .service-color-bar{height:4px;border-radius:100px;margin-bottom:16px}
        .service-name{font-size:1rem;font-weight:700;color:#1e293b;margin-bottom:6px}
        .service-desc{font-size:.82rem;color:#64748b;line-height:1.5;margin-bottom:14px}
        .service-footer{display:flex;align-items:center;justify-content:space-between}
        .service-price{font-size:1.3rem;font-weight:800;color:#1e293b}
        .service-dur{font-size:.75rem;color:#94a3b8;margin-top:2px}

        /* STAFF */
        .staff-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:16px}
        .staff-card{background:white;border-radius:16px;padding:24px 16px;text-align:center;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,.04)}
        .staff-avatar{width:72px;height:72px;border-radius:50%;background:linear-gradient(135deg,#3b82f6,#8b5cf6);display:flex;align-items:center;justify-content:center;font-size:1.5rem;font-weight:700;color:white;margin:0 auto 14px;overflow:hidden;border:3px solid #e2e8f0}
        .staff-avatar img{width:100%;height:100%;object-fit:cover}
        .staff-name{font-size:.95rem;font-weight:700;color:#1e293b;margin-bottom:6px}
        .staff-bio{font-size:.8rem;color:#64748b;line-height:1.5}

        /* GALLERY */
        .gallery-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:12px}
        .gallery-item{aspect-ratio:1;border-radius:12px;overflow:hidden;cursor:pointer;position:relative}
        .gallery-item img{width:100%;height:100%;object-fit:cover;transition:transform .3s}
        .gallery-item:hover img{transform:scale(1.05)}

        /* LIGHTBOX */
        .lightbox{position:fixed;inset:0;background:rgba(0,0,0,.92);z-index:200;display:flex;align-items:center;justify-content:center;animation:fadeIn .2s ease}
        .lightbox img{max-width:90vw;max-height:90vh;border-radius:12px;object-fit:contain}
        .lightbox-close{position:absolute;top:20px;right:24px;color:white;font-size:2rem;cursor:pointer;line-height:1}

        /* EMPTY */
        .empty{text-align:center;padding:60px 24px;color:#94a3b8}
        .empty-icon{font-size:48px;margin-bottom:16px}
        .empty-title{font-size:1.1rem;font-weight:600;color:#64748b;margin-bottom:6px}

        /* MODAL */
        .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:100;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px);animation:fadeIn .2s ease}
        .modal-box{background:white;border-radius:24px;padding:32px;max-width:460px;width:100%;box-shadow:0 40px 100px rgba(0,0,0,.25);animation:fadeUp .25s ease;max-height:90vh;overflow-y:auto}
        .modal-title{font-size:1.2rem;font-weight:800;color:#1e293b;margin-bottom:4px}
        .modal-sub{font-size:.85rem;color:#64748b;margin-bottom:24px}
        .modal-summary{background:#f8fafc;border-radius:12px;padding:16px;margin-bottom:24px}
        .modal-row{display:flex;justify-content:space-between;font-size:.85rem;padding:4px 0}
        .modal-label{color:#64748b}
        .modal-value{font-weight:600;color:#1e293b}
        .form-group{margin-bottom:16px}
        .form-label{display:block;font-size:.82rem;font-weight:600;color:#475569;margin-bottom:6px}
        .form-input{width:100%;padding:11px 14px;border:1px solid #e2e8f0;border-radius:10px;font-size:.9rem;color:#1e293b;outline:none;transition:border-color .2s}
        .form-input:focus{border-color:#3b82f6;box-shadow:0 0 0 3px rgba(59,130,246,.1)}
        .form-input::placeholder{color:#cbd5e1}
        .modal-actions{display:flex;gap:12px;margin-top:8px}
        .btn-cancel{flex:1;padding:12px;border-radius:10px;border:1px solid #e2e8f0;background:white;color:#64748b;font-weight:600;font-size:.9rem;cursor:pointer;transition:background .2s}
        .btn-cancel:hover{background:#f8fafc}
        .btn-confirm{flex:2;padding:12px;border-radius:10px;border:none;background:#3b82f6;color:white;font-weight:700;font-size:.9rem;cursor:pointer;transition:background .2s}
        .btn-confirm:hover{background:#2563eb}
        .btn-confirm:disabled{background:#93c5fd;cursor:not-allowed}
        .btn-book{background:#3b82f6;color:white;border:none;padding:10px 20px;border-radius:10px;font-size:.82rem;font-weight:700;cursor:pointer;transition:background .2s,transform .15s;white-space:nowrap}
        .btn-book:hover{background:#2563eb;transform:translateY(-1px)}

        /* FOOTER */
        .pub-footer{background:#1e293b;color:#64748b;text-align:center;padding:28px 24px;font-size:.8rem;margin-top:60px}
        .pub-footer a{color:#3b82f6;text-decoration:none}

        @media(max-width:640px){
          .pub-cover{height:180px}
          .pub-header-inner{flex-direction:column;align-items:flex-start;transform:translateY(-24px)}
          .pub-logo-wrap{width:64px;height:64px;font-size:1.4rem}
          .pub-name{font-size:1.2rem}
          .event-card{flex-direction:column}
          .event-date-col{flex-direction:row;gap:8px;padding:12px 16px;min-width:unset}
          .pub-tab{padding:12px 14px;font-size:.8rem}
        }
      `}</style>

      {/* NAVBAR */}
      <div className="pub-navbar">
        <a href="https://fitpulse.es" className="pub-navbar-logo">● FitPulse</a>
        <a href="https://admin.fitpulse.es/register" className="pub-navbar-cta">Registra tu negocio</a>
      </div>

      {/* COVER */}
      <div className="pub-cover">
        {biz.coverImage ? <img src={biz.coverImage} alt={biz.name} /> : null}
        <div className="pub-cover-overlay"></div>
      </div>

      {/* HEADER */}
      <div className="pub-header">
        <div className="pub-header-inner">
          <div className="pub-logo-wrap">
            {biz.logo ? <img src={biz.logo} alt={biz.name} /> : biz.name.charAt(0).toUpperCase()}
          </div>
          <div className="pub-header-info">
            {biz.category && <div className="pub-category">{biz.category}</div>}
            <h1 className="pub-name">{biz.name}</h1>
            <div className="pub-meta">
              {biz.city && <span className="pub-meta-item">📍 {biz.city}</span>}
              {biz.phone && <a href={`tel:${biz.phone}`} className="pub-meta-item" style={{textDecoration:'none',color:'#64748b'}}>📞 {biz.phone}</a>}
              {biz.email && <a href={`mailto:${biz.email}`} className="pub-meta-item" style={{textDecoration:'none',color:'#64748b'}}>✉️ {biz.email}</a>}
              {biz.website && <a href={biz.website} target="_blank" rel="noreferrer" className="pub-meta-item" style={{textDecoration:'none',color:'#64748b'}}>🌐 Web</a>}
            </div>
            <div className="pub-socials">
              {biz.instagram && <a href={`https://instagram.com/${biz.instagram}`} target="_blank" rel="noreferrer" className="pub-social">📸</a>}
              {biz.facebook && <a href={biz.facebook} target="_blank" rel="noreferrer" className="pub-social">👥</a>}
              {biz.tiktok && <a href={`https://tiktok.com/@${biz.tiktok}`} target="_blank" rel="noreferrer" className="pub-social">🎵</a>}
              {biz.whatsapp && <a href={`https://wa.me/${biz.whatsapp}`} target="_blank" rel="noreferrer" className="pub-social">💬</a>}
            </div>
          </div>
        </div>
        {biz.description && (
          <div style={{maxWidth:960,margin:'0 auto',padding:'0 24px 20px',fontSize:'.9rem',color:'#475569',lineHeight:1.6}}>
            {biz.description}
          </div>
        )}
      </div>

      {/* TABS */}
      <div className="pub-tabs">
        <div className="pub-tabs-inner">
          {([
            ['eventos', `📅 Eventos (${biz.events.length})`],
            ['servicios', `🎯 Servicios (${biz.services.length})`],
            ['equipo', `👥 Equipo (${biz.staff.length})`],
            ...(gallery.length > 0 ? [['galeria', `🖼️ Galería (${gallery.length})`]] : []),
          ] as [string,string][]).map(([key, label]) => (
            <button key={key} className={`pub-tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key as any)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="pub-body">
        {done && (
          <div className="success-box">
            ✅ ¡Inscripción completada! Recibirás una confirmación en tu email pronto.
          </div>
        )}

        {/* EVENTOS */}
        {tab === 'eventos' && (
          biz.events.length > 0 ? (
            <div className="events-grid">
              {biz.events.map(e => {
                const d = new Date(e.startTime);
                return (
                  <div key={e.id} className="event-card">
                    <div className="event-date-col">
                      <div className="event-day">{d.getDate()}</div>
                      <div className="event-mon">{d.toLocaleDateString('es-ES',{month:'short'})}</div>
                    </div>
                    <div className="event-body">
                      <div>
                        <div className="event-title">{e.title}</div>
                        {e.description && <div className="event-desc">{e.description}</div>}
                        <div className="event-tags">
                          <span className="tag tag-gray">🕐 {fmtT(e.startTime)} – {fmtT(e.endTime)}</span>
                          <span className="tag tag-gray">👥 {e.maxCapacity} plazas</span>
                          {e.service && <span className="tag tag-blue">🎯 {e.service.name}</span>}
                          {e.service?.price && <span className="tag tag-green">{e.service.price}{sym}</span>}
                          {e.staff && <span className="tag tag-gray">👤 {e.staff.firstName}</span>}
                        </div>
                      </div>
                      <div className="event-footer">
                        <span className="event-time">📅 {fmt(e.startTime)}</span>
                        <button className="btn-book" onClick={() => setBookingEvent(e)}>Inscribirme</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty">
              <div className="empty-icon">📅</div>
              <div className="empty-title">Sin eventos próximos</div>
              <p>Este negocio no tiene eventos programados por ahora.</p>
            </div>
          )
        )}

        {/* SERVICIOS */}
        {tab === 'servicios' && (
          biz.services.length > 0 ? (
            <div className="services-grid">
              {biz.services.map(s => (
                <div key={s.id} className="service-card">
                  <div className="service-color-bar" style={{background: s.color || '#3b82f6'}}></div>
                  <div className="service-name">{s.name}</div>
                  {s.description && <div className="service-desc">{s.description}</div>}
                  <div className="service-footer">
                    <div>
                      <div className="service-price">{s.price}{sym}</div>
                      <div className="service-dur">⏱ {s.duration} min</div>
                    </div>
                    <button className="btn-book" onClick={() => setBookingService(s)}>Reservar</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">
              <div className="empty-icon">🎯</div>
              <div className="empty-title">Sin servicios publicados</div>
            </div>
          )
        )}

        {/* EQUIPO */}
        {tab === 'equipo' && (
          biz.staff.length > 0 ? (
            <div className="staff-grid">
              {biz.staff.map(s => (
                <div key={s.id} className="staff-card">
                  <div className="staff-avatar">
                    {s.avatar ? <img src={s.avatar} alt={s.firstName}/> : s.firstName.charAt(0)}
                  </div>
                  <div className="staff-name">{s.firstName} {s.lastName}</div>
                  {s.bio && <div className="staff-bio">{s.bio}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">
              <div className="empty-icon">👥</div>
              <div className="empty-title">Sin equipo publicado</div>
            </div>
          )
        )}

        {/* GALERÍA */}
        {tab === 'galeria' && (
          gallery.length > 0 ? (
            <div className="gallery-grid">
              {gallery.map((img, i) => (
                <div key={i} className="gallery-item" onClick={() => setLightbox(img)}>
                  <img src={img} alt={`Foto ${i+1}`} loading="lazy"/>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">
              <div className="empty-icon">🖼️</div>
              <div className="empty-title">Sin fotos todavía</div>
            </div>
          )
        )}
      </div>

      {/* LIGHTBOX */}
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <span className="lightbox-close">✕</span>
          <img src={lightbox} alt="Foto" onClick={e => e.stopPropagation()}/>
        </div>
      )}

      {/* MODAL INSCRIPCIÓN EVENTO */}
      {bookingEvent && (
        <div className="modal-overlay" onClick={() => setBookingEvent(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Inscribirse al evento</div>
            <div className="modal-sub">Rellena tus datos para confirmar la plaza</div>
            <div className="modal-summary">
              <div className="modal-row"><span className="modal-label">Evento</span><span className="modal-value">{bookingEvent.title}</span></div>
              <div className="modal-row"><span className="modal-label">Fecha</span><span className="modal-value">{fmt(bookingEvent.startTime)}</span></div>
              <div className="modal-row"><span className="modal-label">Hora</span><span className="modal-value">{fmtT(bookingEvent.startTime)} – {fmtT(bookingEvent.endTime)}</span></div>
              {bookingEvent.service?.price && <div className="modal-row"><span className="modal-label">Precio</span><span className="modal-value">{bookingEvent.service.price}{sym}</span></div>}
            </div>
            <div className="form-group">
              <label className="form-label">Nombre completo *</label>
              <input className="form-input" placeholder="Tu nombre" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" placeholder="tu@email.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-input" placeholder="+34 600 000 000" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Notas (opcional)</label>
              <textarea className="form-input" placeholder="Alguna pregunta o comentario..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} style={{resize:'none'}}/>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setBookingEvent(null)}>Cancelar</button>
              <button className="btn-confirm" disabled={sending || !form.name || !form.email} onClick={handleBook}>
                {sending ? 'Enviando...' : 'Confirmar inscripción'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL RESERVA SERVICIO */}
      {bookingService && (
        <div className="modal-overlay" onClick={() => setBookingService(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Reservar servicio</div>
            <div className="modal-sub">Rellena tus datos y nos pondremos en contacto contigo</div>
            <div className="modal-summary">
              <div className="modal-row"><span className="modal-label">Servicio</span><span className="modal-value">{bookingService.name}</span></div>
              <div className="modal-row"><span className="modal-label">Duración</span><span className="modal-value">{bookingService.duration} min</span></div>
              <div className="modal-row"><span className="modal-label">Precio</span><span className="modal-value">{bookingService.price}{sym}</span></div>
            </div>
            <div className="form-group">
              <label className="form-label">Nombre completo *</label>
              <input className="form-input" placeholder="Tu nombre" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" placeholder="tu@email.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-input" placeholder="+34 600 000 000" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Notas (opcional)</label>
              <textarea className="form-input" placeholder="Cuándo te viene bien, preferencias..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} style={{resize:'none'}}/>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setBookingService(null)}>Cancelar</button>
              <button className="btn-confirm" disabled={sending || !form.name || !form.email} onClick={handleBook}>
                {sending ? 'Enviando...' : 'Enviar solicitud'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pub-footer">
        Powered by <a href="https://fitpulse.es">FitPulse</a> · La plataforma de negocios fitness y bienestar en España
      </div>
    </>
  );
}
